import React from 'react';
import { Col, Row } from 'antd';
import { MdCancel } from 'react-icons/md';

import CustomModal from './CustomModal';
import Text from '../Text';
import { Colors } from '../../config';

/**
 * Confirmation dialog for an action the user should be able to think twice about.
 *
 * Exists because these were being done with `window.confirm`, which is the browser's
 * dialog, not the app's: unstyled, unthemed, ignores dark mode, cannot show a second
 * line of explanation, blocks the main thread, and on some mobile browsers is
 * suppressed outright — so a confirmation that never appears becomes an action that
 * silently does not happen.
 *
 * The layout deliberately matches the `CustomModal` confirmations already in the chat
 * panes (delete thread, leave group) rather than the older `Confirmation.jsx`, so a
 * destructive prompt looks the same wherever it comes from.
 *
 * `tone="danger"` fills the confirm button with `--danger` rather than the brand accent.
 * The brand here is red too, so a destructive action styled with the accent reads as the
 * ordinary primary action — which is exactly the wrong signal on a button that cannot be
 * undone.
 *
 * @param {boolean} open
 * @param {string} title
 * @param {React.ReactNode} body        - the question; a node so it can carry a caveat
 * @param {string} [confirmText='Yes']
 * @param {string} [cancelText='No']
 * @param {'danger'|'accent'} [tone='accent']
 * @param {boolean} [loading=false]     - disables the confirm while a request is in flight
 * @param {() => void} onCancel
 * @param {() => void} onConfirm
 */
const ConfirmDialog = ({
  open,
  title,
  body,
  confirmText = 'Yes',
  cancelText = 'No',
  tone = 'accent',
  loading = false,
  onCancel,
  onConfirm,
}) => {
  const confirmBackground = tone === 'danger' ? 'var(--danger)' : 'var(--accent)';
  const confirmForeground = tone === 'danger' ? 'var(--danger-fg)' : Colors.White;

  return (
    <CustomModal
      open={open}
      cancelModal={onCancel}
      className={'custom-modal'}
      centered={true}
      width={450}
      Children={
        <>
          <div className="flex justify-between w-full items-center mb-6">
            <Text.Basic
              className="flex-auto text-center text-[#2B3674]"
              style={{ fontWeight: '600' }}
              text={title}
            />
            <MdCancel
              className="cursor-pointer"
              color={Colors.ModalCancelColor}
              size={20}
              onClick={onCancel}
            />
          </div>

          {/* A node, not a string: the caveat under the question ("notifications already
              delivered cannot be recalled") is the part that stops an admin being
              surprised, and `Text.Basic` cannot express two weights. */}
          <div
            className="w-full text-center"
            style={{ color: 'var(--text)', fontSize: '15px', lineHeight: 1.6 }}
          >
            {body}
          </div>

          <Row className="w-full">
            <Col span={12}>
              <input
                type="button"
                value={cancelText}
                className="w-11/12 mt-6 mr-2 h-11 rounded cursor-pointer hover:opacity-95"
                style={{
                  backgroundColor: Colors.White,
                  color: 'var(--accent)',
                  borderColor: 'var(--accent)',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                }}
                onClick={onCancel}
              />
            </Col>
            <Col span={12}>
              <input
                type="button"
                value={confirmText}
                disabled={loading}
                className="w-11/12 mt-6 ml-2 h-11 rounded cursor-pointer hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ backgroundColor: confirmBackground, color: confirmForeground }}
                onClick={onConfirm}
              />
            </Col>
          </Row>
        </>
      }
    />
  );
};

export default ConfirmDialog;
